import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { app_perm_set_line_RowClass, PermissionAssigneeTargetLinkContainerModel, PermissionSetContainerModel, PermissionSetService, PermissionTemplateService, TenantPermissionAssigneeTargetService } from 'blg-akaun-ts-lib';
import { fetchAllByCriteria } from '../../../../../../listing.utils';
import { ToastrService } from 'ngx-toastr';
import { AkaunConfirmationDialogComponent } from '../../../../../../dialogues/akaun-confirmation-dialog/akaun-confirmation-dialog';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { PermissionSetModel } from '../../../../../../models/perm-set.model';
import { ViewColumnComponent } from '../../../../../../view-column.component';
import { AppConfig } from '../../../../../../visa';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermSetActions } from '../../../../perm-set-controller/actions';
import { PermSetSelectors } from '../../../../perm-set-controller/selectors';
import { PermSetStates } from '../../../../perm-set-controller/states';
import { PermissionStates } from '../../../../permission-controller/root';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  deactivateTemplate: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-perm-set-edit',
  templateUrl: './perm-set-edit.component.html',
  styleUrls: ['./perm-set-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermSetEditComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 2;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.selectedIndex));

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;
  masterVisa = AppConfig.masterVisa;

  public form: FormGroup;

  // keep track of form being enabled/disabled
  formDisabled: boolean = false;

  // no leakage of subscribe calls
  private subSink = new SubSink;

  // fields that require options
  status: string;
  statusArr: any = [];
  newStatusArr: any = [];

  selectedPermSet: PermissionSetModel;

  // to confirm whether user wants to delete a permission set
  akaunConfirmationDialogComponentMatRef: MatDialogRef<AkaunConfirmationDialogComponent>;

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  delete_button: string;
  perm_set_name_text: string;
  please_insert_name_text: string;
  perm_set_code_text: string;
  perm_template_text: string;
  not_a_valid_entry_text: string;
  configure_permission_targets_text: string;
  please_configure_permission_targets_text: string;
  description_text: string;
  status_text: string;
  active_text: string;
  inactive_text: string;

  constructor(
    public dialog: MatDialog,
    private permTemplateService: PermissionTemplateService,
    private permSetService: PermissionSetService,
    private tenantPermAssigneeTargetLinkService: TenantPermissionAssigneeTargetService,
    private toasterService: ToastrService,
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<LocalState>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.edit_permission_set;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.delete_button = res.delete_button_nocaps;
        this.perm_set_name_text = res.perm_set_name;
        this.please_insert_name_text = res.please_insert_name;
        this.perm_set_code_text = res.perm_set_code;
        this.perm_template_text = res.perm_template;
        this.not_a_valid_entry_text = res.invalid_entry;
        this.configure_permission_targets_text = res.configure_permission_targets;
        this.please_configure_permission_targets_text = res.please_configure_permission_targets;
        this.description_text = res.description;
        this.status_text = res.status;
        this.active_text = res.active;
        this.inactive_text = res.inactive;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Edit Permission Set';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.delete_button = 'Delete';
      this.perm_set_name_text = 'Perm Set Name';
      this.please_insert_name_text = 'Please insert name';
      this.perm_set_code_text = 'Perm Set Code';
      this.perm_template_text = 'Perm Template';
      this.not_a_valid_entry_text = 'Not a valid entry';
      this.configure_permission_targets_text = 'Configure Permission Targets';
      this.please_configure_permission_targets_text = 'Please configure permission targets!';
      this.description_text = 'Description';
      this.status_text = 'Status';
      this.active_text = 'ACTIVE';
      this.inactive_text = 'INACTIVE';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.statusArr = [
      { value: 'ACTIVE', viewValue: this.active_text },  // Use the translated value
      { value: 'INACTIVE', viewValue: this.inactive_text }  // Use the translated value
    ];
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      // uneditable
      code: [''],
      description: [''],
      currStatus: [''],
      status: ['', Validators.compose([Validators.required])],
      // uneditable
      permTemplate: [''],
      configureTargets: ['']
    })

    this.newStatusArr = this.statusArr;

    const permSet$ = this.store.select(PermSetSelectors.selectPermSet);

    // display permission set
    this.subSink.sink = permSet$
      .subscribe(permSet => {
        this.selectedPermSet = permSet;

        // patch form values
        this.subSink.sink = this.getPermTemplate(permSet.app_perm_set_hdr.parent_guid.toString())
        .subscribe(permTemplate => {
          this.form.patchValue({
            name: permSet.app_perm_set_hdr.name,
            code: permSet.app_perm_set_hdr.code,
            description: permSet.app_perm_set_hdr.description,
            status: permSet.app_perm_set_hdr.status,
            permTemplate: permTemplate.app_perm_template_hdr.name,
            // targets are always configured because you cannot delete a perm set line
            // and a permission set always already has configured targets- we can only update it
            // FOR FUTURE ENHANCEMENT, need to double check if the perm template has been updated
            // then we need to re-configure
            configureTargets: 'CONFIGURED'
          })
        })
      })
  }

  /**
   * Get the permission template with guid = permTemplateGuid.
   *
   * @param permTemplateGuid : permission template to get
   * @returns an observable containing the permission template
   */
  getPermTemplate(permTemplateGuid: string) {
    return this.permTemplateService.getByGuid(permTemplateGuid, this.masterVisa)
      .pipe(
        map(x => x.data)
      );
  }

  /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyStatusFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newStatusArr = this.statusArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Update the permission set and all perm assignee target links.
   *
   */
  onSubmit() {
    // update perm set name, description and status
    this.selectedPermSet.app_perm_set_hdr.name = this.form.value.name;
    this.selectedPermSet.app_perm_set_hdr.description = this.form.value.description;
    this.selectedPermSet.app_perm_set_hdr.status = this.form.value.status;

    // final permission set container model
    const permSet = new PermissionSetContainerModel();
    permSet.app_perm_set_hdr = this.selectedPermSet.app_perm_set_hdr;
    permSet.app_perm_set_lines = this.selectedPermSet.permSetLines.map(x => x.app_perm_set_line);

    // dispatch ngrx actoin to update permission set
    this.store.dispatch(PermSetActions.updatePermSetInit({permSet: permSet}));

    // get the original permission set and find the lines that have been updated
    this.subSink.sink = this.getPermSet()
    .subscribe(permSet => {
      const changedLines: app_perm_set_line_RowClass[] = [];

      const originalLines = permSet.app_perm_set_lines.reduce((acc, x) => {
        acc[x.guid.toString()] = x;
        return acc;
      }, {})


      this.selectedPermSet.permSetLines.forEach(line => {
        const newTarget = line.app_perm_set_line.target_hdr_guid;
        const originalTarget = originalLines[line.app_perm_set_line.guid.toString()]['target_hdr_guid'];

        if (newTarget != originalTarget) {
          changedLines.push(line.app_perm_set_line);
        }
      });

      // dispatch ngrx action to update the perm assignee target links that are associated with these lines
      if (changedLines.length > 0) {
        this.store.dispatch(PermSetActions.updatePermAssigneeTargetLinksInit({lines: changedLines}));
      }

    })
  }

  /**
   * Gets the original perm set.
   *
   * @returns
   */
  getPermSet() {
    return this.permSetService.getByGuid(this.selectedPermSet.app_perm_set_hdr.guid.toString(), this.apiVisa)
      .pipe(map(x => x.data))
  }

  /**
   * Configure targets in perm set lines.
   */
  selectTemplate() {
    // update perm set name, description and status
    this.selectedPermSet.app_perm_set_hdr.name = this.form.value.name;
    this.selectedPermSet.app_perm_set_hdr.description = this.form.value.description;
    this.selectedPermSet.app_perm_set_hdr.status = this.form.value.status;

    // dispatch ngrx action to save perm set
    this.store.dispatch(PermSetActions.selectPermSet({permSet: this.selectedPermSet}));

    if (!this.localState.deactivateTemplate) {
      this.viewColFacade.updateInstance<LocalState>(this.index, {
        ...this.localState,
        deactivateTemplate: true,
        deactivateReturn: true
      });
      this.viewColFacade.onNext(6);
    }
  }

  /**
   * Delete permission set and all the related permission assignee target links.
   */
  onDelete() {
    const permAssigneeGuids$ = this.getPermAssigneeTargetGuids();

    this.subSink.sink = permAssigneeGuids$
      .subscribe(permAssigneeGuids => {

        if (permAssigneeGuids.length > 0) {
          this.akaunConfirmationDialogComponentMatRef = this.dialog.open(
            AkaunConfirmationDialogComponent,
            {width: '400px'}
          );
          this.akaunConfirmationDialogComponentMatRef.componentInstance.confirmMessage = 'This permission set is being used. Are you sure you want to delete?';
          this.akaunConfirmationDialogComponentMatRef
            .afterClosed()
            .subscribe(result => {
              if (result == true) {
                // dispatch ngrx action to delete perm assignee target links and permission set
                this.store.dispatch(PermSetActions.deletePermAssigneeTargetLinksInit({permSetHdrGuid: this.selectedPermSet.app_perm_set_hdr.guid.toString()}));
                this.store.dispatch(PermSetActions.deletePermSetInit({guid: this.selectedPermSet.app_perm_set_hdr.guid.toString()}));
                this.onReturn();
              }
            })
        } else {
          // delete permisison set only
          this.store.dispatch(PermSetActions.deletePermSetInit({guid: this.selectedPermSet.app_perm_set_hdr.guid.toString()}));
          this.onReturn();
        }

      })
  }

  /**
   * Return the guids of the permission assignee target links that are related to this permission set.
   *
   * @returns the guids of the permission assignee target links that are related to this permission set.
   */
   getPermAssigneeTargetGuids() {
    /**
     * Replaces the raw SELECT that went through SubQueryService:
     *
     *   SELECT DISTINCT(hdr.guid) FROM app_perm_assignee_target_link hdr
     *   WHERE hdr.perm_set_hdr_guid = '<perm set guid>' AND hdr.status != 'DELETED'
     *
     * GET permission-assignee-target/query ->
     * PermissionAssigneeTargetLinkUow.getByCriteria ->
     * generateWhereClauseByCriteria honours `perm_set_hdr_guid` and defaults to
     * `status != 'DELETED'`, so both halves of the WHERE clause become criteria.
     * The caller only checks whether the list is non-empty, but the guids are
     * still returned so the shape of this method is unchanged.
     *
     * fetchAllByCriteria pages until the endpoint stops returning full pages, so
     * the result is the complete set of links, not just the first page.
     */
    return fetchAllByCriteria<PermissionAssigneeTargetLinkContainerModel>(
      pagination => this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa),
      [{
        columnName: 'perm_set_hdr_guid',
        operator: '=',
        value: this.selectedPermSet.app_perm_set_hdr.guid.toString()
      }]
    ).pipe(
      map(links => links.map(link => link.permAssigneeTargetLink.guid.toString()))
    )
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateTemplate: true,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
    // reset ngrx states
    this.store.dispatch(PermSetActions.selectPermSet({permSet: null}));
    this.store.dispatch(PermSetActions.selectPermSetLine({permSetLine: null}));
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
   ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance<LocalState>(this.index, {
        ...this.localState,
        deactivateTemplate: true,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
